import { ThunkDispatch } from "@reduxjs/toolkit";
import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
	Button,
	Modal,
	ModalHeader,
	ModalBody,
	ModalFooter,
	Input,
	InputGroup,
	InputGroupText,
	Table,
	Offcanvas,
	OffcanvasHeader,
	OffcanvasBody,
} from "reactstrap";
import { Link } from "react-router-dom";
import { Action } from "redux";
import { ApplicationState } from "store";
import { reportDetailGetRequest } from "store/report/action";
import DtsTablePlaceholder from "Components/Common/FormBuilder/DtsTablePlaceholder";
import Paginator from "Components/Common/PaginatorFix";
import processFilterDateType from "helpers/filter_datetime";
import moment from "moment";

interface ReportProps {
	config: any;
	detailReportData: any;
	setDetailReportData: (detailReportData: any) => void;
	t: (key: string) => string;
}

interface SortDescriptor {
	key: string;
	order: "asc" | "desc";
}

const ReportDetails: React.FC<ReportProps> = ({
	t,
	config,
	detailReportData,
	setDetailReportData,
}) => {
	const dispatch: ThunkDispatch<any, null, Action<string>> = useDispatch();

	const [reportConfig, setReportConfig] = useState<any>(null);
	const [pagination, setPagination] = useState({
		elements: 0,
		offset: 0,
		size: 10,
		page: 1,
		pages: 1,
	});
	const [showFilters, setShowFilters] = useState(false);
	const [rawSearch, setRawSearch] = useState("");
	const [searchQuery, setSearchQuery] = useState("");
	const [sorting, setSorting] = useState<SortDescriptor[]>([]);
	const [searchColumn, setSearchColumn] = useState<string>("All");

	const generatedReport = useSelector(
		(state: ApplicationState) => state.report.generated_report_detail
	) || { data: [] };

	const loading = useSelector(
		(state: ApplicationState) => state.report.generated_report_detail_loading
	);

	const paginationResponse =
		useSelector((state: ApplicationState) => state.report.pagination_detail) ||
		pagination;

	// 2) On “open” (i.e. when detailReportData becomes non-null) call resetAll()
	useEffect(() => {
		if (detailReportData) {
			resetAll();
		}
	}, [config, detailReportData]);

	// debounce typing
	useEffect(() => {
		const h = setTimeout(() => setSearchQuery(rawSearch), 500);
		return () => clearTimeout(h);
	}, [rawSearch]);

	// rebuild filters + condition when search changes
	// replace your current “rebuild filters & condition” effect with this:

	useEffect(() => {
		if (!reportConfig) return;

		// always start from your base config
		const base = processFilterDateType(reportConfig?.filters) || [];
		const origCondition = reportConfig.filter_condition || "";
		let filters = [...base];
		let newCondition = origCondition;

		if (searchQuery) {
			const baseCount = filters.length;

			if (searchColumn === "All") {
				const textCols = reportConfig?.columns?.filter((c: { type: string; }) =>
					["textarea", "textfield"].includes(c.type)
				);

				// append one LIKE filter per column
				textCols.forEach((col: { key: any; data_source: any; json_field: any; }) =>
					filters.push({
						key: col.key,
						condition: "LIKE",
						value: searchQuery,
						data_source: col.data_source,
						json_field: col.json_field,
					})
				);

				// build the “OR(…)” clause
				const positions = textCols.map((_: any, i: number) => baseCount + i + 1);
				const orClause = `(${positions.join(" OR ")})`;
				newCondition = origCondition
					? `(${origCondition}) AND ${orClause}`
					: orClause;
			} else {
				// single‐column filter
				const col = reportConfig?.columns?.find((c: { key: string; }) => c.key === searchColumn);
				if (col) {
					filters.push({
						key: col.key,
						condition: "LIKE",
						value: searchQuery,
						data_source: col.data_source,
						json_field: col.json_field,
					});
					const clause = `(${baseCount + 1})`;
					newCondition = origCondition
						? `(${origCondition}) AND ${clause}`
						: clause;
				}
			}

			// only dispatch when there's a real search
			dispatch(reportDetailGetRequest({
				...reportConfig,
				filters,
				filter_condition: newCondition,
			}));
		} else {
			// no searchQuery → just re-fetch with your un‐modified config
			dispatch(reportDetailGetRequest(reportConfig));
		}
	}, [reportConfig, dispatch, searchQuery, searchColumn]);



	// keep pagination in sync
	useEffect(() => setPagination(paginationResponse), [paginationResponse]);

	// 1) Extract a single “reset everything” function:
	const resetAll = () => {
		// clear search UI
		setRawSearch("");
		setSearchQuery("");
		setSearchColumn("All");
		// clear sorting UI
		setSorting([]);

		// rebuild filters + condition exactly as on mount/open
		const existingBaseFilters = config?.filters?.filter((f: any) => !f.detail_field) || [];
		const origCond = config?.filter_condition || "";

		let baseFilters = [...existingBaseFilters];

		// Add detail filters if present
		if (detailReportData && detailReportData.key !== "all" && !detailReportData.all) {
			baseFilters = [...baseFilters, ...detailReportData]; // detailReportData must be an array
		}

		// Generate merged condition like: (1) AND (2) AND (3)
		const mergedCondition = baseFilters
			.map((_, idx) => `(${idx + 1})`) // position is 1-based
			.join(" AND ");

		// reset the redux‐fetch configuration
		setReportConfig({
			...config,
			filters: baseFilters,
			filter_condition: mergedCondition || "1",
			chart_type: "table",
			columns: config.fields,
			limit: reportConfig?.limit || 10,  // preserve page‐size
			page: 1,
			sorting: [],                       // wipe out any server‐side sort
		});
	};

	const pageChanged = (val: any) => {
		if (!reportConfig) return;
		let newPage = reportConfig.page;
		const lastPage = pagination.pages;
		if (val === "prev") newPage = Math.max(1, newPage - 1);
		else if (val === "next") newPage = Math.min(lastPage, newPage + 1);
		else if (val === "first") newPage = 1;
		else if (val === "last") newPage = lastPage;
		else if (typeof val === "number") newPage = val;

		setReportConfig({ ...reportConfig, page: newPage });
		setPagination({ ...pagination, page: newPage });
	};

	const onChangePageSize = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const size = Number(e.target.value);
		if (!reportConfig) return;
		setReportConfig({ ...reportConfig, limit: size });
		setPagination({ ...pagination, size });
	};



	// 3) Your old handleReset now just calls resetAll()
	const handleReset = () => {
		resetAll();
	};


	const renderCellContent = (raw: any, col: any): React.ReactNode => {
		// 1) HTML snippet?
		if (typeof raw === "string" && raw.trim().startsWith("<")) {
			return <div dangerouslySetInnerHTML={{ __html: raw }} />;
		}
		// 2) datetime?
		if (col.type === "datetime") {
			const formatted = moment(raw).isValid()
				? moment(raw).format("lll")
				: "-";
			return formatted;
		}
		// 3) fallback plain
		return raw;
	}


	return (
		<>
			<Offcanvas isOpen={showFilters} toggle={() => setShowFilters(false)}>
				<OffcanvasHeader toggle={() => setShowFilters(false)}>
					{t("Filters")}
				</OffcanvasHeader>
				<OffcanvasBody>
					{config?.filters?.map((f: any, i: number) => (
						<div key={i} className="mb-3">
							<label className="form-label fw-bold text-secondary">
								{f.key}
							</label>
							<div className="ms-2">{String(f.value)}</div>
						</div>
					))}
				</OffcanvasBody>
			</Offcanvas>

			<Modal
				size="xl"
				isOpen={!!detailReportData}
				toggle={() => setDetailReportData(null)}
				className="modal-fullscreen"
			>
				<ModalHeader
					toggle={() => setDetailReportData(null)}
					className="border-bottom px-4 py-3"
				>
					Report Details
				</ModalHeader>

				<ModalBody className="p-0 d-flex flex-column" style={{ height: "100%" }}>
					{!reportConfig?.columns?.length ? (
						<div className="text-center p-5 border rounded bg-light m-4">
							<i className="ri-file-info-line fs-2 text-muted mb-2 d-block" />
							<h6 className="fw-semibold text-muted">No fields configured</h6>
							<p className="text-muted small mb-0">
								This report has no visible fields. Please configure fields to view data.
							</p>
						</div>
					) : (
						<>
							{/* Pagination & Toolbar */}
							<div className="d-flex justify-content-between align-items-center px-4 py-2">
								<small className="text-muted">
									{`${pagination.offset + 1}-${Math.min(
										pagination.offset + pagination.size,
										pagination.elements
									)} of ${pagination.elements}`}
								</small>
								<Paginator
									total={pagination.elements}
									currentPage={pagination.page}
									pageChanged={pageChanged}
									pageSize={pagination.size}
									isPageSizeChange
									onChangeInSelect={onChangePageSize}
									pagination={pagination}
								/>
							</div>

							{/* Toolbar */}
							<div className="bg-light border d-flex align-items-center px-4 py-2">
								<InputGroup size="sm" className="me-auto" style={{ maxWidth: 350 }}>
									{/* 2.1 Column selector */}
									<Input
										type="select"
										value={searchColumn}
										onChange={(e) => setSearchColumn(e.target.value)}
										style={{ maxWidth: 120 }}
									>
										<option value="All">All</option>
										{reportConfig.columns
											.filter((c: any) => c.type === "textarea" || c.type === "textfield")
											.map((c: any) => (
												<option key={c.key} value={c.key}>
													{c.label || c.key}
												</option>
											))}
									</Input>

									{/* 2.2 The actual text input */}
									<Input
										placeholder={
											searchColumn === "All"
												? "Search all..."
												: `Search in ${searchColumn}`
										}
										value={rawSearch}
										onChange={(e) => setRawSearch(e.target.value)}
									/>

									<InputGroupText>
										<i className="ri-search-line" />
									</InputGroupText>
								</InputGroup>


								<div className="d-flex gap-1">
									{/* <Button
                                        color="primary"
                                        className="btn-label btn-sm"
                                        onClick={() => setShowFilters(true)}
                                    >
                                        <i className="ri-equalizer-line label-icon align-middle fs-16 me-2"></i>
                                        Filter
                                    </Button> */}

									<Button
										color="danger"
										className="btn-label btn-sm"
										onClick={handleReset}
									>
										<i className="ri-refresh-line label-icon align-middle fs-16 me-2"></i>
										Reset
									</Button>
								</div>
							</div>

							{/* Scrollable Table */}
							{loading ? (
								<DtsTablePlaceholder
									columns={reportConfig?.columns}
									tableClass="align-middle table-nowrap"
									thClass="table-light"
								/>
							) : (
								<div className="flex-fill table-responsive">
									<Table className="table table-nowrap mb-0">
										<thead className="table-light">
											{reportConfig.columns.map((col: any, idx: number) => (
												<th
													key={idx}
													className="bg-light py-2 px-4 position-sticky top-0"
													style={{ cursor: "pointer", userSelect: "none" }}
													onClick={(e) => {
														const isMulti = e.ctrlKey || e.metaKey;
														const existing = sorting.find(s => s.key === col.key);
														const nextOrder = existing
															? (existing.order === "asc" ? "desc" : "asc")
															: "asc";

														let nextSorting: SortDescriptor[];
														if (isMulti) {
															if (existing) {
																// toggle only that column
																nextSorting = sorting.map(s =>
																	s.key === col.key ? { key: col.key, order: nextOrder } : s
																);
															} else {
																// append new column
																nextSorting = [...sorting, { key: col.key, order: nextOrder }];
															}
														} else {
															// single-column sort
															nextSorting = [{ key: col.key, order: nextOrder }];
														}

														setSorting(nextSorting);

														if (reportConfig) {
															setReportConfig({
																...reportConfig,
																page: 1,  // reset to first page
																sorting: nextSorting.map(s => ({
																	key: s.key,
																	direction: s.order,
																	data_source: config.fields.find((f: { key: string; }) => f.key === s.key)!.data_source,
																	json_field: config.fields.find((f: { key: string; }) => f.key === s.key)!.json_field || false,
																	type: config.fields.find((f: { key: string; }) => f.key === s.key)!.type,
																}))
															});
														}
													}}
												>
													{col.label || col.key}
													{(() => {
														const desc = sorting.find(s => s.key === col.key);
														return desc ? (desc.order === "asc" ? " ↑" : " ↓") : "";
													})()}
												</th>
											))}
										</thead>
										<tbody>
											{generatedReport?.data?.map((row: any, ridx: number) => (
												<tr key={ridx}>
													{reportConfig.columns.map((col: any, cidx: number) => {
														const raw = col.data_source.primary
															? row[col.key]
															: row[col.data_source.value]?.[col.key];

														// 1) empty/null
														if (raw == null || raw === "") {
															return (
																<td key={cidx} className="px-4 text-muted fst-italic">
																	–
																</td>
															);
														}

														// 2) first‐column record link
														if (cidx === 0 && col.data_source.primary && row.record_id) {
															return (
																<td key={cidx} className="px-4 fw-semibold">
																	<Link
																		to={`/${col.data_source.value}/${row.record_id}`}
																		className="text-primary"
																	>
																		{renderCellContent(raw, col)}
																	</Link>
																</td>
															);
														}

														// 3) datasetselect / treeselect link
														if (
															(col.type === "datasetselect" || col.type === "treeselect") &&
															row[col.key + "_id"]
														) {
															return (
																<td key={cidx} className="px-4 fw-semibold">
																	<Link
																		to={`/${col.model}/${row[col.key + "_id"]}`}
																		target="_blank"
																		rel="noopener noreferrer"
																	>
																		{renderCellContent(raw, col)}
																	</Link>
																</td>
															);
														}

														// 4) textarea or textfield (plain editable text)
														if (col.type === "textarea" || col.type === "textfield") {
															return (
																<td
																	key={cidx}
																	className="px-4"
																	style={col.type === "textarea" ? { whiteSpace: "normal" } : {}}
																>
																	{renderCellContent(raw, col)}
																</td>
															);
														}

														// 5) all other columns
														return (
															<td key={cidx} className="px-4">
																{renderCellContent(raw, col)}
															</td>
														);
													})}
												</tr>
											))}
										</tbody>

									</Table>
								</div>
							)}
						</>
					)}
				</ModalBody>


				<ModalFooter className="border-top d-flex justify-content-between align-items-center px-4 py-2">
					<small className="text-muted">
						{`${pagination.offset + 1}-${Math.min(
							pagination.offset + pagination.size,
							pagination.elements
						)} of ${pagination.elements}`}
					</small>

					<Paginator
						total={pagination.elements}
						currentPage={pagination.page}
						pageChanged={pageChanged}
						pageSize={pagination.size}
						isPageSizeChange
						onChangeInSelect={onChangePageSize}
						pagination={pagination}
					/>
				</ModalFooter>
			</Modal>
		</>
	);
};

export default withTranslation()(ReportDetails);

